import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, Divider, IconButton, List, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { HidingError } from "components/errors";
import Paginator, { useNormalizePage } from "components/paginator";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetLicencesQuery, useUpdateLicenceMutation } from "services/data/licenceApi";
import styles from "./LicencePage.module.css";
import LicenceGenerator from "./LicenceGenerator";
import LicenceItem from "./LicenceItem";
import { resetLicence, setExpandedBar, setPagination } from "store/licence/licenceSlice";
import { AccessTime, AccessTimeOutlined, AccountCircle, AccountCircleOutlined, AppsOutlined, Block, ExpandMore, FileDownload, HideSource, HourglassBottom, HourglassTop, RemoveCircle, ShoppingBagOutlined } from "@mui/icons-material";
import { monthEnding } from "utils/date";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { ConfirmationDialog } from "components/dialogs";
import { useGetProfilesQuery } from "services/data/profileApi";
import { useGetAppsQuery } from "services/data/appApi";
import { useQueryStale } from "rtkq/useQueryStale";
import { getDefaultQuerySearch, getManagerSearch } from "store/searchbar/selectors";
import { resetManagerSearch, setManagerSearch } from "store/searchbar/searchbarSlice";
import { markEndpointStale } from "rtkq/staleManager";
import { FullScreenLoader } from "components/loaders";
import { CircularProgress } from "components/overlays";
import BoxStyled from "components/forms/SearchBar/styled/BoxStyled";
import IconButtonStyled from "components/forms/SearchBar/styled/IconButtonStyled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMaximize, faMinimize } from "@fortawesome/free-solid-svg-icons";
import ToggleButtonStyled from "components/forms/SearchBar/styled/ToggleButtonStyled";


const DEFAULT_ERROR = "Ошибка соединения с сервером";

const LicencePage = ({setActiveToolbar, defaultSearch, setExtra}) => {

  useEffect( () => setActiveToolbar('licence'), []); 

  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef(null);

  const dispatch = useDispatch();

  const { selected, pagination, expandedBar } = useSelector(state => state.licence);
  const search = useSelector((state) => getManagerSearch(state.searchbar, 'licence', defaultSearch.licence)); 
  const queryArgs = {
      ...search,
      pagination: {start: pagination.start, limit: pagination.limit}
  };

  const [updateLicence, {error: updateLicenceError, reset: updateLicenceReset}] = useUpdateLicenceMutation();

  

  const profsQueryArgs = getDefaultQuerySearch(defaultSearch.profile);
  const appsQueryArgs = getDefaultQuerySearch(defaultSearch.app);  

  const profsQuery = useGetProfilesQuery(profsQueryArgs);
  const { data: profilesData, isError: isGetProfilesError, 
          isFetching: isGetProfilesFetching, refetch: isGetProfilesRefetch } = profsQuery;
  useQueryStale({query: profsQuery, endpointName: 'getProfiles', queryArgs: profsQueryArgs});          
  
  const appsQuery = useGetAppsQuery(appsQueryArgs);
  const { data: appsData, isError: isGetAppsError, isFetching: isGetAppsFetching,
           refetch: isGetAppsRefetch } = appsQuery;
  useQueryStale({query: appsQuery, endpointName: 'getApps', queryArgs: appsQueryArgs});

  const licenceQuery = useGetLicencesQuery(queryArgs);
  const { data, isError, error, isFetching, refetch } = licenceQuery;
  useQueryStale({query: licenceQuery, endpointName: 'getLicences', queryArgs: queryArgs});

  useNormalizePage({data, pagination, actionCreator: setPagination}); 


  const renderExpander = useMemo(() => {
    return  <Box sx={{display: 'flex', alignItems: 'stretch', justifyContent: 'center', margin: '0 8px'}}>
              <BoxStyled className={styles.expander}>
                <ToggleButtonGroup
                  exclusive
                  value={expandedBar}
                  onChange={(e, value) => value && (dispatch(setExpandedBar(value)))}
                  sx={{height: '100%'}}
                >
                  <ToggleButtonStyled value='expanded'>
                      <FontAwesomeIcon icon={faMaximize} />
                  </ToggleButtonStyled>  
                  <ToggleButtonStyled value='collapsed'>
                      <FontAwesomeIcon icon={faMinimize} />
                  </ToggleButtonStyled>
                </ToggleButtonGroup>
              </BoxStyled>
              
            </Box>
    
  }, [expandedBar]);

  useEffect( () => {
    setExtra(renderExpander);
      
    return () => setExtra(null);
  }, [renderExpander, setExtra]);

  useEffect( () => {
    resetScroll();
  }, [data?.body?.items.length]);


  
  //нужно перепрыгнуть на 1 страницу, сбросить фильтр, вернуть дефолтную сортировку, пометить все оставшиеся кэши устаревшими
  //оптимистичный апдейт не нужен, т.к. при добавлении лицензии мы не знаем, на какой странице она окажется, а значит, не можем обновить кэш
  
  const resetFitersAndSort = useCallback(() => {
    changePage(1);
    const defaultQuerySearch = getDefaultQuerySearch(defaultSearch.licence); 
    if(defaultQuerySearch.sort) {
      //сбрасываем фильтр и ставим сортировку по-умолчанию
      dispatch(
        resetManagerSearch( {active: 'licence', sort: defaultQuerySearch.sort[0]} )
      );
    }
    dispatch(
        markEndpointStale( {endpointName: 'getLicences'} )
    );
  }, [defaultSearch]);

  const changePage = (page) => {
      const start = (page - 1) * pagination.limit; 
      dispatch(setPagination({...pagination, start: start, page: page}));
      dispatch(resetLicence());
      setExpanded(false);
  };

  const openError = () => {  
    return !!updateLicenceError || false;
  };

  const getErrorMessage = () => { 
    if(updateLicenceError) return updateLicenceError?.data?.message ?? DEFAULT_ERROR;
    else return'Ошибка';
  };

  const hideError = () => {
    if(updateLicenceError) updateLicenceReset();
  };  

  const resetScroll = () => {
    bodyRef?.current && bodyRef.current.scrollTo({top: 0, behavior: 'smooth'});
  };

  
  const selectLicence = (id) => (event, isExpanded) => {
    setExpanded(isExpanded ? id : false);
  };

  const onEdit = useCallback( (params) => {
    updateLicence({...params, currentQueryArgs: queryArgs});
  }, [queryArgs]);
 

const renderEmpty = <Card sx={{maxHeight: '100%', display: 'flex', flexDirection: 'column'}}>
                            <CardContent sx={{overflow: 'auto', display: 'flex', justifyContent: 'center'}}>
                                <div>Лицензий пока нет</div>
                            </CardContent>
                        </Card>;

  const renderItems = (items) => {
    
    if((!items || items.length < 1) && !isFetching) {
      return renderEmpty;
    }

    //if(isFetching) return <div>Загрузка...</div>;
    //if(isFetching) return <FullScreenLoader />;

    //if(pagination.start ===20) return <FullScreenLoader />;

    return items?.map((item, index) => {
      return (
        <LicenceItem 
          //key={item.id} 
          key={`${item.id}-${expandedBar}`}
          item={item} 
          expandedBar={expandedBar}
          expanded={expanded} 
          selectLicence={selectLicence}
          onEdit={onEdit}
        />
      );
    });
    
  };


  //if(isFetching) return <div>Загрузка...</div>;
  if(isError) return <div>{error?.data?.message ?? "Ошибка!"}</div>; 
  
  return (
    <div className="page">
      <HidingError 
        open={ openError() }
        message={ getErrorMessage() }
        onClose={hideError}
      />
      <div className={styles.overlayWrapper}>
        <div className="page-content">
          <div className="left">
            <div className="pagelist">
              <div className="pagelist-body" ref={bodyRef}>
                <CircularProgress open={isFetching}/>
                {renderItems(data?.body?.items)}
              </div>
              <Paginator 
                  changePage={(e, page) => changePage(page)}
                  total={data?.body?.total ?? 0}
                  pageSize={pagination.limit}
                  page={pagination.page}
              />  
            </div>
          </div>    
          <div className="right">
            <LicenceGenerator
              profiles={profilesData?.body?.items}
              apps={appsData?.body?.items}
              resetFitersAndSort={resetFitersAndSort}
            />
          </div>
        </div>
      </div> 
    </div>
  );


};

export default LicencePage;